import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { listProductions } from '../api';
import type { Production } from '../types/db';
import type { PageOutletContext } from '../components/PageLayout';

const STATUS_CONFIG: Record<Production['status'], { label: string; classes: string }> = {
  active: { label: 'Attiva', classes: 'bg-emerald-100 text-emerald-700' },
  paused: { label: 'In pausa', classes: 'bg-amber-100 text-amber-700' },
  archived: { label: 'Archiviata', classes: 'bg-slate-100 text-slate-600' },
};

export default function Dashboard() {
  const { credits } = useOutletContext<PageOutletContext>();
  const [productions, setProductions] = useState<Production[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const capReached =
    credits != null && credits.active_productions_count >= credits.max_active_productions;

  useEffect(() => {
    let cancelled = false;
    listProductions()
      .then((p) => {
        if (!cancelled) setProductions(p);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Errore nel caricamento');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Le tue testate</h1>
        {capReached ? (
          <span
            className="cursor-not-allowed rounded-lg bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-500"
            title="Cap del piano raggiunto. Archivia una testata oppure fai upgrade del piano per crearne una nuova."
          >
            + Nuova testata
          </span>
        ) : (
          <Link
            to="/dashboard/productions/new"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            + Nuova testata
          </Link>
        )}
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-slate-500">Caricamento…</p>
      ) : error ? (
        <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          Errore: {error}
        </div>
      ) : productions.length === 0 ? (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">Nessuna testata trovata.</p>
          <Link
            to="/dashboard/productions/new"
            className="mt-4 inline-block text-sm font-medium text-brand-600 hover:underline"
          >
            Crea la prima
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {productions.map((p) => (
            <Link
              key={p.id}
              to={`/dashboard/production/${p.id}`}
              className="block rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-brand-500 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-slate-900">{p.name}</h3>
                  <p className="mt-0.5 text-xs text-slate-500">{p.brand_name}</p>
                </div>
                <span
                  className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CONFIG[p.status].classes}`}
                >
                  {STATUS_CONFIG[p.status].label}
                </span>
              </div>

              <p className="mt-3 truncate text-xs text-slate-500">{p.base_url}</p>

              <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
                {p.output_audio && <span>🎧 Audio</span>}
                {p.output_vodcast && <span>🎬 Vodcast</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
